import React from 'react';
import { useNavigate } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import {
  Download, FileText, Shield, Check, Lock, Eye, UserCheck, Database, Mail, MapPin, Clock,
} from 'lucide-react';

// PrivacyPolicy — Swiss Minimalist + Pastel theme.
// Pastels: sage #D4E0DA · peach #EBD8C8 · lavender #D9CFD9
// Fonts: .font-display · .font-body · .font-label

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  const highlights = [
    { tint: '#D4E0DA', title: 'Data minimisation',  description: 'We only collect information necessary to provide and improve our services.' },
    { tint: '#EBD8C8', title: 'Secure storage',     description: 'AES-256 encryption, secure cloud infrastructure, and regular security audits.' },
    { tint: '#D9CFD9', title: 'Your rights',        description: 'Access, correct, delete, or export your personal data at any time.' },
    { tint: '#D4E0DA', title: 'No selling of data', description: 'We never sell your personal information to third parties.' },
    { tint: '#EBD8C8', title: 'Transparency',       description: 'Clear communication about data usage and third-party services.' },
    { tint: '#D9CFD9', title: 'Cookie control',     description: 'You control which cookies and tracking technologies we can use.' },
  ];

  const coreTopics = [
    { icon: Eye,       label: 'What information we collect and why' },
    { icon: Database,  label: 'How we use and store your data' },
    { icon: UserCheck, label: 'Your privacy rights and choices' },
    { icon: Shield,    label: 'Security measures and data protection' },
  ];

  const moreTopics = [
    'Third-party services and integrations',
    'Cookie usage and tracking technologies',
    'International data transfers',
    "Children's privacy protection",
    'Data retention and deletion policies',
    'Changes to this Privacy Policy',
  ];

  const collected = [
    { icon: UserCheck, tint: '#D4E0DA', title: 'Account information', description: 'Name, email address, workspace details, and authentication credentials.' },
    { icon: Database,  tint: '#EBD8C8', title: 'Usage data',          description: 'How you interact with our platform, features used, and session information.' },
    { icon: Shield,    tint: '#D9CFD9', title: 'Compliance data',     description: 'Information you provide for compliance assessments and certifications.' },
  ];

  return (
    <div className="min-h-screen bg-[#FAFAFA] dark:bg-[#050505] text-[#0A0A0A] dark:text-[#F5F5F5] font-body">
      <Header />

      {/* ===================== HERO ===================== */}
      <section
        className="bg-[#D9CFD9]/55 dark:bg-[#1E1724]/55 pt-24 md:pt-32 pb-24 md:pb-32 px-6 border-b border-black/10 dark:border-white/10 min-h-[480px] md:min-h-[640px]"
        data-testid="privacy-hero"
      >
        <div className="max-w-5xl mx-auto text-center w-full">
          <p className="font-label text-[11px] uppercase tracking-[0.3em] text-black/55 dark:text-white/55 mb-8">
            Your Privacy
          </p>
          <h1 className="font-display font-semibold leading-[1.25] tracking-tight text-2xl sm:text-3xl lg:text-4xl mb-8">
            Privacy
            <br />
            <span className="italic text-black/85 dark:text-white/90">Policy.</span>
          </h1>
          <p className="font-body text-base md:text-lg text-black/70 dark:text-white/70 max-w-3xl mx-auto leading-relaxed mb-10">
            How we collect, use, and protect your personal information — written in plain language, governed by GDPR, CCPA, and global data‑protection laws.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button
              type="button"
              onClick={() => window.open('/ethira-privacy-policy.pdf', '_blank')}
              data-testid="privacy-download-hero"
              className="font-label bg-[#0A0A0A] dark:bg-[#141414] text-[#FAFAFA] px-8 py-4 rounded-full text-xs uppercase tracking-[0.2em] hover:bg-[#D4E0DA] dark:hover:bg-[#101F18] hover:text-[#0A0A0A] dark:hover:text-[#F5F5F5] transition-all duration-500 inline-flex items-center gap-2"
            >
              <Download className="w-3.5 h-3.5" strokeWidth={1.5} />
              Download Full Policy
            </button>
            <button
              type="button"
              onClick={() => navigate('/contact')}
              data-testid="privacy-contact"
              className="font-label border border-[#0A0A0A]/20 dark:border-white/25 text-[#0A0A0A] dark:text-[#F5F5F5] px-8 py-4 rounded-full text-xs uppercase tracking-[0.2em] hover:border-[#0A0A0A] dark:hover:border-white/30 hover:bg-[#EBD8C8]/40 dark:hover:bg-[#2E1B10]/40 transition-all duration-500"
            >
              Contact Privacy Team
            </button>
          </div>
        </div>
      </section>

      {/* ===================== OVERVIEW + DOC INFO ===================== */}
      <section className="px-6 py-24 md:py-28" data-testid="privacy-overview">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-[1.2fr_1fr] gap-12 lg:gap-16 items-start">
          <div>
            <p className="font-label text-[11px] uppercase tracking-[0.25em] text-black/55 dark:text-white/55 mb-4">
              Overview
            </p>
            <h2 className="font-display font-semibold text-3xl md:text-4xl tracking-tight mb-8 leading-tight">
              Your privacy, <span className="italic text-black/85 dark:text-white/90">in plain language.</span>
            </h2>
            <div className="space-y-5 text-base md:text-lg text-black/75 dark:text-white/75 leading-relaxed">
              <p>
                At <strong className="text-[#0A0A0A] dark:text-[#F5F5F5]">Nova</strong> (operated by <strong className="text-[#0A0A0A] dark:text-[#F5F5F5]">Inceptica LLC</strong>), we are committed to protecting your privacy and being transparent about how we collect, use, and safeguard your personal information. This Privacy Policy applies to all users of our AI-powered compliance and certification platform and related services.
              </p>
              <p>
                By using our services, you agree to the practices described here. We comply with <strong className="text-[#0A0A0A] dark:text-[#F5F5F5]">GDPR, CCPA</strong>, and other applicable data‑protection laws to ensure your information is handled with the highest standards of security and privacy.
              </p>
            </div>
          </div>

          <div className="rounded-2xl bg-[#D4E0DA]/45 dark:bg-[#101F18]/45 p-8 md:p-10">
            <div className="flex items-start gap-3 mb-5">
              <FileText className="w-5 h-5 mt-0.5 text-black/65 dark:text-white/65" strokeWidth={1.5} />
              <p className="font-label text-[11px] uppercase tracking-[0.25em] text-black/55 dark:text-white/55">
                Document Information
              </p>
            </div>
            <div className="space-y-5">
              <div>
                <p className="font-label text-[10px] uppercase tracking-[0.22em] text-black/55 dark:text-white/55 mb-1">Effective Date</p>
                <p className="font-display text-lg md:text-xl leading-tight">November 28, 2025</p>
              </div>
              <div className="border-t border-black/10 dark:border-white/10 pt-5">
                <p className="font-label text-[10px] uppercase tracking-[0.22em] text-black/55 dark:text-white/55 mb-1">Last Updated</p>
                <p className="font-display text-lg md:text-xl leading-tight">November 2025</p>
              </div>
              <div className="border-t border-black/10 dark:border-white/10 pt-5">
                <p className="font-label text-[10px] uppercase tracking-[0.22em] text-black/55 dark:text-white/55 mb-1">Applies To</p>
                <p className="font-display text-lg md:text-xl leading-tight">All Nova customers and visitors</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===================== KEY HIGHLIGHTS (BENTO) ===================== */}
      <section className="px-6 pb-24 md:pb-32" data-testid="privacy-highlights">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <p className="font-label text-[11px] uppercase tracking-[0.25em] text-black/55 dark:text-white/55 mb-3">
              Key Privacy Highlights
            </p>
            <h2 className="font-display font-semibold text-3xl md:text-4xl tracking-tight mb-4">
              What you can <span className="italic text-black/85 dark:text-white/90">count on.</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 border-t border-l border-black/10 dark:border-white/10">
            {highlights.map((h, i) => (
              <div
                key={h.title}
                data-testid={`privacy-highlight-${i}`}
                className="group relative border-r border-b border-black/10 dark:border-white/10 p-8 md:p-10 transition-colors duration-700 flex flex-col"
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = h.tint + '40'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = ''; }}
              >
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center mb-8 nv-tint"
                  style={{ backgroundColor: h.tint + 'CC' }}
                >
                  <Lock className="w-5 h-5 text-[#0A0A0A] dark:text-[#F5F5F5]" strokeWidth={1.5} />
                </div>
                <p
                  className="font-display font-semibold leading-none text-5xl md:text-6xl mb-5 select-none"
                  style={{ color: h.tint }}
                >
                  {String(i + 1).padStart(2, '0')}
                </p>
                <h3 className="font-display text-lg md:text-xl leading-tight mb-3">{h.title}</h3>
                <p className="font-body text-sm md:text-base text-black/65 dark:text-white/65 leading-relaxed">{h.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===================== WHAT'S COVERED ===================== */}
      <section className="px-6 pb-24 md:pb-32" data-testid="privacy-covered">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <p className="font-label text-[11px] uppercase tracking-[0.25em] text-black/55 dark:text-white/55 mb-3">
              What's Covered
            </p>
            <h2 className="font-display font-semibold text-3xl md:text-4xl tracking-tight mb-4 leading-tight">
              The full scope.
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6 md:gap-8">
            <div className="rounded-2xl border border-black/10 dark:border-white/10 p-8 md:p-10 bg-[#FAFAFA] dark:bg-[#050505]">
              <p className="font-label text-[10px] uppercase tracking-[0.22em] text-black/55 dark:text-white/55 mb-5">
                Core Topics
              </p>
              <ul className="space-y-4">
                {coreTopics.map(({ icon: Icon, label }) => (
                  <li key={label} className="flex items-start gap-3">
                    <Icon className="w-4 h-4 mt-1 shrink-0 text-black/55 dark:text-white/55" strokeWidth={1.5} />
                    <span className="font-body text-base text-black/75 dark:text-white/75 leading-relaxed">{label}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-2xl border border-black/10 dark:border-white/10 p-8 md:p-10 bg-[#FAFAFA] dark:bg-[#050505]">
              <p className="font-label text-[10px] uppercase tracking-[0.22em] text-black/55 dark:text-white/55 mb-5">
                Additional Details
              </p>
              <ul className="space-y-3">
                {moreTopics.map((t) => (
                  <li key={t} className="flex items-start gap-3">
                    <Check className="w-4 h-4 mt-1 shrink-0 text-black/55 dark:text-white/55" strokeWidth={1.5} />
                    <span className="font-body text-sm md:text-base text-black/75 dark:text-white/75 leading-relaxed">{t}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ===================== INFORMATION WE COLLECT ===================== */}
      <section className="px-6 pb-24 md:pb-32" data-testid="privacy-info-collected">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <p className="font-label text-[11px] uppercase tracking-[0.25em] text-black/55 dark:text-white/55 mb-3">
              Information We Collect
            </p>
            <h2 className="font-display font-semibold text-3xl md:text-4xl tracking-tight mb-4 leading-tight">
              Three categories — <span className="italic text-black/85 dark:text-white/90">nothing more.</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {collected.map(({ icon: Icon, tint, title, description }, i) => (
              <div
                key={title}
                data-testid={`privacy-collected-${i}`}
                className="rounded-2xl border border-black/10 dark:border-white/10 p-8 md:p-10 hover:border-black/20 dark:hover:border-white/20 transition-colors duration-500"
              >
                <div
                  className="w-14 h-14 rounded-2xl flex items-center justify-center mb-5 nv-tint"
                  style={{ backgroundColor: tint + 'CC' }}
                >
                  <Icon className="w-6 h-6 text-[#0A0A0A] dark:text-[#F5F5F5]" strokeWidth={1.5} />
                </div>
                <h3 className="font-display text-xl md:text-2xl leading-tight mb-3">{title}</h3>
                <p className="font-body text-sm md:text-base text-black/65 dark:text-white/65 leading-relaxed">{description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===================== CONTACT ===================== */}
      <section className="px-6 pb-24 md:pb-32" data-testid="privacy-contact-section">
        <div className="max-w-5xl mx-auto rounded-2xl bg-[#EBD8C8]/45 dark:bg-[#2E1B10]/45 p-8 md:p-12">
          <div className="grid lg:grid-cols-[1fr_1.2fr] gap-10 items-start">
            <div>
              <p className="font-label text-[11px] uppercase tracking-[0.25em] text-black/55 dark:text-white/55 mb-3">
                Questions about privacy?
              </p>
              <h3 className="font-display font-semibold text-2xl md:text-4xl tracking-tight leading-tight">
                Reach our Privacy Officer.
              </h3>
            </div>
            <ul className="space-y-5">
              <li className="flex items-start gap-4">
                <Mail className="w-4 h-4 mt-1.5 shrink-0 text-black/65 dark:text-white/65" strokeWidth={1.5} />
                <div>
                  <p className="font-label text-[10px] uppercase tracking-[0.22em] text-black/55 dark:text-white/55 mb-1">Email</p>
                  <a href="mailto:privacy@teamnova.ai" className="font-display text-lg md:text-xl leading-tight underline-offset-4 hover:underline">privacy@teamnova.ai</a>
                </div>
              </li>
              <li className="flex items-start gap-4">
                <MapPin className="w-4 h-4 mt-1.5 shrink-0 text-black/65 dark:text-white/65" strokeWidth={1.5} />
                <div>
                  <p className="font-label text-[10px] uppercase tracking-[0.22em] text-black/55 dark:text-white/55 mb-1">Address</p>
                  <p className="font-body text-base text-black/80 dark:text-white/85 leading-relaxed">2389 Main St. STE 100, Glastonbury, CT 06033</p>
                </div>
              </li>
              <li className="flex items-start gap-4">
                <Clock className="w-4 h-4 mt-1.5 shrink-0 text-black/65 dark:text-white/65" strokeWidth={1.5} />
                <div>
                  <p className="font-label text-[10px] uppercase tracking-[0.22em] text-black/55 dark:text-white/55 mb-1">Response Time</p>
                  <p className="font-body text-base text-black/80 dark:text-white/85 leading-relaxed">Within 48 hours · 24 hours for urgent matters</p>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* ===================== FINAL CTA (INVERTED) ===================== */}
      <section className="bg-[#0A0A0A] dark:bg-[#141414] py-24 md:py-32 px-6 text-center" data-testid="privacy-cta-section">
        <div className="max-w-4xl mx-auto">
          <p className="font-label text-[11px] uppercase tracking-[0.3em] text-white/65 mb-6">
            Download
          </p>
          <h2 className="font-display font-semibold text-3xl md:text-4xl text-[#FAFAFA] mb-6 leading-[1.1] tracking-tight">
            Read the full policy.
          </h2>
          <p className="font-body text-sm md:text-base text-white/70 mb-12 max-w-2xl mx-auto">
            Detailed information about our data practices, your rights, and how we protect your information.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button
              type="button"
              onClick={() => window.open('/ethira-privacy-policy.pdf', '_blank')}
              data-testid="privacy-cta-download"
              className="font-label bg-[#FAFAFA] dark:bg-[#050505] text-[#0A0A0A] dark:text-[#F5F5F5] hover:bg-[#D9CFD9] dark:hover:bg-[#1E1724] uppercase tracking-[0.2em] text-xs px-8 py-4 rounded-full transition-all duration-500 inline-flex items-center gap-2"
            >
              <Download className="w-3.5 h-3.5" strokeWidth={1.5} />
              Download Privacy Policy (PDF)
            </button>
            <button
              type="button"
              onClick={() => navigate('/security')}
              data-testid="privacy-cta-security"
              className="font-label border border-white/25 bg-transparent text-[#FAFAFA] hover:bg-[#FAFAFA] dark:hover:bg-[#050505] hover:text-[#0A0A0A] dark:hover:text-[#F5F5F5] uppercase tracking-[0.2em] text-xs px-8 py-4 rounded-full transition-all duration-500"
            >
              Our Security Practices
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PrivacyPolicy;
